/**
 * FeedbackModal Component
 *
 * Modal dialog for submitting feedback, bug reports, and feature requests.
 * Automatically captures a screenshot of the page when opened.
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Chip,
  IconButton,
  Collapse
} from '@mui/material';
import { useAuth } from '../../context/AuthContext';
import { captureScreenshot, getScreenshotSize } from '../../utils/screenshotHelper';
import { APP_VERSION } from '../../constants';

// Icons
import CloseIcon from '@mui/icons-material/Close';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

const FeedbackModal = ({ open, onClose }) => {
  const { authFetch } = useAuth();

  // Form state
  const [type, setType] = useState('bug');
  const [priority, setPriority] = useState('medium');
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');

  // Screenshot state
  const [screenshot, setScreenshot] = useState(null);
  const [screenshotLoading, setScreenshotLoading] = useState(false);
  const [showScreenshot, setShowScreenshot] = useState(false);
  const [hiddenForScreenshot, setHiddenForScreenshot] = useState(false);

  // UI state
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  // Auto-capture screenshot when modal opens
  useEffect(() => {
    if (open && !screenshot) {
      captureScreenshotAuto();
    }
  }, [open]);

  const captureScreenshotAuto = async () => {
    setScreenshotLoading(true);
    setHiddenForScreenshot(true);
    setError('');

    // Wait for dialog to be hidden from DOM before capturing
    await new Promise(r => setTimeout(r, 50));

    try {
      const screenshotData = await captureScreenshot({
        scale: 0.7,
        maxWidth: 1600,
        quality: 0.8
      });

      setScreenshot(screenshotData);

      // Log screenshot size
      const sizeMB = getScreenshotSize(screenshotData);
      console.log(`Screenshot captured: ${sizeMB.toFixed(2)} MB`);

    } catch (err) {
      console.error('Failed to capture screenshot:', err);
      setError('Screenshot konnte nicht erstellt werden. Du kannst trotzdem fortfahren.');
    } finally {
      setScreenshotLoading(false);
      setHiddenForScreenshot(false);
    }
  };

  const handleRetakeScreenshot = async () => {
    setScreenshot(null);
    await captureScreenshotAuto();
  };

  const handleSubmit = async () => {
    // Validate
    if (!subject.trim()) {
      setError('Bitte gib einen Betreff ein');
      return;
    }

    if (!description.trim()) {
      setError('Bitte gib eine Beschreibung ein');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      // Gather metadata
      const pageUrl = window.location.href;
      const userAgent = navigator.userAgent;

      // Submit feedback
      const response = await authFetch('/api/feedback/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          type,
          priority,
          subject: subject.trim(),
          description: description.trim(),
          screenshot,
          pageUrl,
          userAgent,
          appVersion: APP_VERSION
        })
      });

      if (response.ok) {
        setSuccess(true);
        // Reset form after brief delay
        setTimeout(() => {
          handleClose();
        }, 2000);
      } else {
        const data = await response.json();
        setError(data.message || 'Fehler beim Senden des Feedbacks');
      }

    } catch (err) {
      console.error('Error submitting feedback:', err);
      setError('Netzwerkfehler beim Senden des Feedbacks');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    // Reset form
    setType('bug');
    setPriority('medium');
    setSubject('');
    setDescription('');
    setScreenshot(null);
    setError('');
    setSuccess(false);
    setShowScreenshot(false);

    onClose();
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'low': return 'success';
      case 'medium': return 'warning';
      case 'high': return 'error';
      case 'critical': return 'error';
      default: return 'default';
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'bug': return '🐛';
      case 'feature': return '💡';
      case 'question': return '❓';
      case 'other': return '📝';
      default: return '📝';
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      sx={hiddenForScreenshot ? { display: 'none' } : undefined}
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6" component="div">
            Feedback geben
          </Typography>
          <IconButton
            edge="end"
            onClick={handleClose}
            aria-label="schließen"
            disabled={submitting}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        {success ? (
          <Alert severity="success" sx={{ mb: 2 }}>
            Feedback erfolgreich gesendet! Vielen Dank für deine Rückmeldung.
          </Alert>
        ) : (
          <>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                {error}
              </Alert>
            )}

            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              {/* Type Selection */}
              <FormControl fullWidth size="small">
                <InputLabel id="feedback-type-label">
                  Typ
                </InputLabel>
                <Select
                  labelId="feedback-type-label"
                  value={type}
                  label="Typ"
                  onChange={(e) => setType(e.target.value)}
                  disabled={submitting}
                >
                  <MenuItem value="bug">
                    <Box display="flex" alignItems="center" gap={1}>
                      <span>🐛</span>
                      <span>Fehler</span>
                    </Box>
                  </MenuItem>
                  <MenuItem value="feature">
                    <Box display="flex" alignItems="center" gap={1}>
                      <span>💡</span>
                      <span>Feature-Wunsch</span>
                    </Box>
                  </MenuItem>
                  <MenuItem value="question">
                    <Box display="flex" alignItems="center" gap={1}>
                      <span>❓</span>
                      <span>Frage</span>
                    </Box>
                  </MenuItem>
                  <MenuItem value="other">
                    <Box display="flex" alignItems="center" gap={1}>
                      <span>📝</span>
                      <span>Sonstiges</span>
                    </Box>
                  </MenuItem>
                </Select>
              </FormControl>

              {/* Priority Selection */}
              <FormControl fullWidth size="small">
                <InputLabel id="feedback-priority-label">
                  Priorität
                </InputLabel>
                <Select
                  labelId="feedback-priority-label"
                  value={priority}
                  label="Priorität"
                  onChange={(e) => setPriority(e.target.value)}
                  disabled={submitting}
                >
                  <MenuItem value="low">
                    <Chip label="Niedrig" color="success" size="small" />
                  </MenuItem>
                  <MenuItem value="medium">
                    <Chip label="Mittel" color="warning" size="small" />
                  </MenuItem>
                  <MenuItem value="high">
                    <Chip label="Hoch" color="error" size="small" />
                  </MenuItem>
                  <MenuItem value="critical">
                    <Chip label="Kritisch" color="error" size="small" />
                  </MenuItem>
                </Select>
              </FormControl>
            </Box>

            {/* Subject */}
            <TextField
              fullWidth
              label="Betreff"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              disabled={submitting}
              required
              sx={{ mb: 2 }}
              placeholder="Kurze Zusammenfassung des Problems oder Wunsches"
            />

            {/* Description */}
            <TextField
              fullWidth
              label="Beschreibung"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              disabled={submitting}
              required
              multiline
              rows={6}
              sx={{ mb: 2 }}
              placeholder="Beschreibe das Problem oder deinen Wunsch im Detail..."
            />

            {/* Screenshot Section */}
            <Box
              sx={{
                p: 2,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                bgcolor: 'background.default'
              }}
            >
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                sx={{ cursor: 'pointer' }}
                onClick={() => setShowScreenshot(!showScreenshot)}
              >
                <Box display="flex" alignItems="center" gap={1}>
                  <CameraAltIcon fontSize="small" color="action" />
                  <Typography variant="body2" fontWeight="medium">
                    Screenshot
                  </Typography>
                  {screenshot && (
                    <Chip
                      label={`${getScreenshotSize(screenshot).toFixed(1)} MB`}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  )}
                </Box>
                <Box display="flex" alignItems="center" gap={1}>
                  {screenshot && !screenshotLoading && (
                    <Button
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRetakeScreenshot();
                      }}
                      disabled={submitting}
                    >
                      Neu aufnehmen
                    </Button>
                  )}
                  <IconButton size="small">
                    {showScreenshot ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </IconButton>
                </Box>
              </Box>

              <Collapse in={showScreenshot}>
                <Box mt={2}>
                  {screenshotLoading ? (
                    <Box display="flex" alignItems="center" justifyContent="center" py={4}>
                      <CircularProgress size={40} />
                      <Typography variant="body2" color="text.secondary" ml={2}>
                        Screenshot wird erstellt...
                      </Typography>
                    </Box>
                  ) : screenshot ? (
                    <Box
                      component="img"
                      src={screenshot}
                      alt="Screenshot"
                      sx={{
                        width: '100%',
                        height: 'auto',
                        maxHeight: 300,
                        objectFit: 'contain',
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1
                      }}
                    />
                  ) : (
                    <Alert severity="info">
                      Kein Screenshot verfügbar
                    </Alert>
                  )}
                </Box>
              </Collapse>
            </Box>

          </>
        )}
      </DialogContent>

      {!success && (
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={handleClose}
            disabled={submitting}
          >
            Abbrechen
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={submitting || !subject.trim() || !description.trim()}
            startIcon={submitting && <CircularProgress size={16} />}
          >
            {submitting ? 'Wird gesendet...' : 'Feedback senden'}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default FeedbackModal;
